import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaClock, FaPlay } from 'react-icons/fa';

const IndividualMockTestInstructionsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subject, testName, duration, testId, testNumber, individualTest } = location.state || {};

  const handleStartTest = () => {
    // Use testNumber to fetch specific test questions
    const testIdToUse = testId || `individual-${subject.toLowerCase()}-${testNumber || 1}`;

    if (subject === 'Writing') {
      navigate('/writing-test', {
        state: {
          testId: testIdToUse,
          testType: 'individual',
          testName: testName,
          testNumber: testNumber || 1,
          individualTest: true
        }
      });
    } else {
      navigate(`/mock-test/${subject.toLowerCase()}`, {
        state: {
          testId: testIdToUse,
          testType: 'individual',
          testName: testName,
          testNumber: testNumber || 1,
          individualTest: true
        }
      });
    }
  };

  const handleBack = () => {
    navigate('/new-student-dashboard');
  };

  const getInstructions = () => {
    const baseInstructions = [
      "Answering questions: Once you select an answer, click the NEXT button to move to the next question.",
      "Question Order: You can answer questions in any order you like.",
      "Changing Answers: you can change your answer at any time during the test, as long as it is within the allocated time.",
      "Final Submission- Once you submit, you will not be able to change your answers.",
    ];

    if (subject === 'Writing') {
      return [
        "Write a complete response to the given prompt",
        "Your response should be 300 words or less",
        "Focus on structure, creativity, and grammar",
        "Check spelling and punctuation before submitting",
        "You will have 30 minutes to complete your writing"
      ];
    }

    if (subject === 'Reading') {
      return [
        ...baseInstructions,
        "Read the passages carefully before answering questions",
        "Manage your time - you have 45 minutes total",
        "Answer all questions to the best of your ability"
      ];
    }

    return baseInstructions;
  };

  const getTestDetails = () => {
    switch (subject) {
      case 'Maths':
        return {
          description: "Mathematical Reasoning test with problem-solving questions",
          questions: "35 multiple-choice questions",
          focus: "Problem solving, calculations, mathematical concepts"
        };
      case 'ThinkingSkills':
        return {
          description: "Thinking Skills test with logical reasoning questions",
          questions: "35 multiple-choice questions",
          focus: "Logical reasoning, critical thinking, problem-solving"
        };
      case 'Reading':
        return {
          description: "Reading comprehension test with passages and questions",
          questions: "35 multiple-choice questions",
          focus: "Reading comprehension, vocabulary, inference"
        };
      case 'Writing':
        return {
          description: "Creative writing test with character description prompt",
          questions: "1 writing task",
          focus: "Creativity, structure, grammar, vocabulary"
        };
      default:
        return {
          description: "Mock test under exam conditions",
          questions: "Multiple-choice questions",
          focus: "Test-taking skills and subject knowledge"
        };
    }
  };

  const testDetails = getTestDetails();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors mb-6"
          >
            <FaChevronLeft className="mr-2" /> Back
          </button>

          {/* Test Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {testName || `${subject} Mock Test`}
            </h1>
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <FaClock className="mr-2" /> Duration: {duration || '40 minutes'}
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {testDetails.description}
            </p>
          </div>

          {/* Test Details */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Test Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {subject === 'Writing' ? '1' : '35'}
                </div>
                <div className="text-sm text-gray-600">Questions</div>
              </div>
              <div className="bg-white p-4 rounded-lg border text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {duration || '40 min'}
                </div>
                <div className="text-sm text-gray-600">Time Limit</div>
              </div>
              {/* <div className="bg-white p-4 rounded-lg border text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {subject === 'Writing' ? 'Essay' : 'MCQ'}
                </div>
                <div className="text-sm text-gray-600">Format</div>
              </div> */}
            </div>
            {/* <div className="mt-4">
              <h4 className="font-semibold text-gray-700 mb-2">Focus Areas:</h4>
              <p className="text-gray-600">{testDetails.focus}</p>
            </div> */}
          </div>

          {/* Important Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <div className="flex flex-col mb-4">
              <h3 className="text-lg font-bold text-yellow-800">
                Welcome to the Online Testing Platform.
              </h3>

              <p className="text-sm font-medium text-yellow-800 mt-2">
                Please read the following instructions carefully before starting your test.
              </p>
            </div>
            <ol className="space-y-3 list-decimal list-inside marker:font-bold">
              {getInstructions().map((instruction, index) => (
                <li key={index} className="text-gray-700">
                  {instruction}
                </li>
              ))}
            </ol>
            <h3 className="mt-2 text-lg font-bold text-yellow-800">
              Good luck and have fun !
            </h3>
          </div>

          {/* Start Test Button */}
          <div className="text-center">
            <button
              onClick={handleStartTest}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:opacity-90 transition-all duration-300 text-lg shadow-lg hover:shadow-xl flex items-center justify-center mx-auto space-x-3"
            >
              <span>Start Test Now</span>
            </button>
            <p className="text-gray-500 text-sm mt-4">
              Once you click "Start Test Now", the timer will begin counting down immediately
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualMockTestInstructionsPage;